import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { KnowledgeBaseService } from '../../../../core/services/knowledge-base.service';
import { MediaService } from '../../../../core/services/media.service';
import { KnowledgeArticleDetail } from '../../../../core/models';
import { resourceTypeLabel, resourceTypeLabelKey } from '../../../../shared/utils/knowledge-article.mapper';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

/**
 * UC-24 : détail d'un article de documentation par slug.
 *
 * `documentId` n'est volontairement pas exploité ici : document-storage-service exige une
 * authentification sur tous ses endpoints (aucun `/public/**`), donc même un article public ne
 * peut pas exposer un lien de téléchargement fonctionnel pour un visiteur anonyme. Seul
 * `mediaAssetId` (media-library-service, public) est résolu.
 */
@Component({
  selector: 'app-documentation-detail',
  imports: [RouterLink, ScrollReveal, TranslatePipe],
  templateUrl: './documentation-detail.html',
})
export class DocumentationDetail {
  private readonly route = inject(ActivatedRoute);
  private readonly knowledgeBase = inject(KnowledgeBaseService);
  private readonly mediaService = inject(MediaService);

  readonly resourceTypeLabelKey = resourceTypeLabelKey;
  /** Repli si le type de ressource n'est pas dans la liste connue (voir `resourceTypeLabelKey`). */
  readonly resourceTypeLabelFallback = resourceTypeLabel;

  readonly loading = signal(true);
  readonly notFound = signal(false);
  readonly article = signal<KnowledgeArticleDetail | null>(null);
  readonly imageUrl = signal<string | null>(null);

  readonly hasUnresolvableDocument = computed(() => {
    const a = this.article();
    return !!a?.documentId && !a.mediaAssetId;
  });

  constructor() {
    const slug = this.route.snapshot.paramMap.get('slug');
    if (!slug) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }
    this.knowledgeBase.publicArticle(slug).subscribe({
      next: (article) => {
        this.article.set(article);
        this.loading.set(false);
        if (article.mediaAssetId) {
          this.mediaService.publicAsset(article.mediaAssetId).subscribe({
            next: (asset) => this.imageUrl.set(asset.publicUrl),
            error: () => undefined,
          });
        }
      },
      error: () => {
        this.loading.set(false);
        this.notFound.set(true);
      },
    });
  }
}
